import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Close,
  Visibility,
} from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useOrderStatusUpdateApi } from '../../../Hooks/useOrderStatusUpdateApi';
import { useTranslation } from 'react-i18next';
import TrackingHistoryTimeline from '../../stateless/TrackingHistoryTimeline';
import { COLORS } from '../../../theme/themeConstants';

interface OrderEditModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  loading?: boolean;
  onSuccess?: () => void;
}


const OrderEditModal: React.FC<OrderEditModalProps> = ({
  open,
  onClose,
  order,
  // loading = false,
  onSuccess
}) => {
  const { t } = useTranslation();
  // console.log('Order for Edit:', order);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selfPickupStatuses, setSelfPickupStatuses] = useState<{ [key: string]: string }>({});
  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);

  const { updateOrderStatus } = useOrderStatusUpdateApi();
  const orderItems = order?.orderItems || [];
  const isOrderCancelled = (
    (order?.currentStatus || '').toLowerCase().includes('cancelled') ||
    order?.merchantControls?.currentPreparationStatus === 'MERCHANT_CANCELLED'
  );

  useEffect(() => {
    if (!orderItems || orderItems.length === 0) return;
  
    const initialStatuses: Record<string, string> = {};
    orderItems.forEach((item:any) => {
      initialStatuses[item.itemId] = item.merchantAccepted
        ? 'Accepted'
        : item.merchantDenied
        ? 'Rejected'
        : 'PENDING';
    });
  
    setSelfPickupStatuses(initialStatuses);
  }, [orderItems]);

  // Combine external loading (from direct API call) with API loading (from status update)
  // const isLoading = externalLoading || apiLoading;

  // console.log('Order Items:', orderItems);

  // Initialize statuses when order changes
  // React.useEffect(() => {
  //   if (order && orderItems.length > 0) {
  //     const initialSelfPickupStatuses: { [key: string]: string } = {};

  //     orderItems.forEach((item: any) => {
  //       // Initialize selfPickupStatuses with current preparation status if available
  //       const currentPreparationStatus = order?.merchantControls?.currentPreparationStatus;
  //       if (currentPreparationStatus && currentPreparationStatus !== 'PENDING') {
  //         initialSelfPickupStatuses[item.itemId] = currentPreparationStatus;
  //       }
  //     });

  //     setSelfPickupStatuses(initialSelfPickupStatuses);
  //   } else {
  //     setSelfPickupStatuses({});
  //   }
  // }, [order, open]);




  // Check if all items are displayed as chips (not dropdowns)
  const areAllItemsDisplayedAsChips = () => {
    if (orderItems.length === 0) return false;

    // Check if all items are displayed as chips (merchantAccepted or merchantDenied is true)
    const result = orderItems.every((item: any) => {
      const isChip = item.merchantAccepted === true || item.merchantDenied === true;
      return isChip;
    });
    
    return result;
  };

  const normalizeStatus = (value: string | undefined | null) => {
    if (!value || value.trim() === '' || value.toUpperCase() === 'PENDING') return 'PENDING';
    return value;
  };
  
  

  const handleSelfPickupChange = (itemId: string, value: string) => {
    setSelfPickupStatuses((prev) => ({
      ...prev,
      [itemId]: normalizeStatus(value),
    }));
  };
  
  

  const handleViewPrescription = () => {
    setPrescriptionDialogOpen(true);
  };

  const handleClosePrescriptionDialog = () => {
    setPrescriptionDialogOpen(false);
  };

  // Check if all items are still in PENDING status
  const areAllItemsPending = () => {
    if (orderItems.length === 0) return true;

    return orderItems.every((item: any) => {
      const currentStatus = selfPickupStatuses[item.itemId] || 'PENDING';
      return currentStatus === 'PENDING';
    });
  };

  // Check if self-pickup order is already completed
  const isSelfPickupOrderCompleted = () => {
    const isSelfPickup = order?.deliveryType === 'selfPickup';
    if (!isSelfPickup) return false;

    const currentStatus = (order?.currentStatus || '').toLowerCase();
    const preparationStatus = order?.merchantControls?.currentPreparationStatus;

    // Check if order is marked as delivered/completed
    const isOrderCompleted = currentStatus.includes('delivered') || 
                            currentStatus.includes('completed') ||
                            preparationStatus === 'MERCHANT_DELIVERED';

    return isOrderCompleted;
  };





  const formatPrice = (price: number) => {
    return typeof price === 'number' ? `JOD ${price.toFixed(2)}` : 'JOD 0.00';
  };

  const calculateItemTotal = (item: any) => {
    // Prioritize discount prices over original price
    const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
    const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
    const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
    const unitPrice = (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
    const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
    
    // Add meal price if present
    const mealPrice = item?.mealPrice ? Number(item.mealPrice) : 0;
    const choicesPrice = item.choices ? item.choices.reduce((sum: number, choice: any) => sum + (Number(choice.choicePrice) || 0), 0) : 0;
    
    // Calculate total: (item price + choices + meal) * quantity
    return (unitPrice + choicesPrice + mealPrice) * quantity;
  };

  if (!order) {
    return null;
  }
  


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Typography variant="h6" fontWeight="bold">
          {t('orderManagement.editOrder.title')} #{order.orderNumber}
        </Typography>
        <Button onClick={onClose} startIcon={<Close />}>
          {/* Close */}
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Order Info Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
              {/* Customer Info */}
              <Box flex={1}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('orderManagement.editOrder.customer')}
                </Typography>
                <Typography variant="h6" fontWeight="medium">
                  {order.customerInfo?.name || order.customerName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {order.customerInfo?.email}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {order.customerInfo?.phoneNumber || order.shippingAddress?.phoneNumber}
                </Typography>
              </Box>

              {/* Order Details */}
              <Box flex={1}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {t('orderManagement.editOrder.orderDetails')}
                </Typography>
                <Box display="grid" rowGap={0.5}>
                  {/* Date */}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">
                      {t('orderManagement.editOrder.date')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {new Date(order.orderDate).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {/* Order Mode & Pre-order highlight */}
                  {order?.orderMode === 'preOrder' && order?.scheduledDeliveryTime ? (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 153, 0, 0.06)',
                        border: '1px dashed',
                        borderColor: 'warning.main',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={0.25}>
                        <Typography variant="body2" color="textSecondary">
                          {t('orderManagement.editOrder.orderModeLabel')}
                        </Typography>
                        <Chip
                          size="small"
                          label={t('orderManagement.orderMode.preOrder')}
                          color="warning"
                          sx={{ fontWeight: 600, alignSelf: 'flex-start' }}
                        />
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <AccessTimeIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ color: 'warning.dark' }}
                        >
                          {new Date(order.scheduledDeliveryTime).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="textSecondary">
                        {t('orderManagement.editOrder.orderModeLabel')}
                      </Typography>
                      <Chip
                        size="small"
                        label={t('orderManagement.orderMode.normal')}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  )}

                  {/* Amount */}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">
                      {t('orderManagement.editOrder.amount')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatPrice(order.orderAmount)}
                    </Typography>
                  </Box>

                  {/* Payment Method */}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">
                      {t('orderManagement.editOrder.payment')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {order.paymentMethod}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Tracking History Section */}
        <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          {/* <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            {t('orderManagement.editOrder.trackingHistory')}
          </Typography> */}
          <TrackingHistoryTimeline 
            trackingHistory={order?.trackingHistory || []}
            currentStatus={order?.currentStatus || ''}
          />
        </Box>

        {/* Prescription Section */}
        {order?.prescriptionUrl && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6" fontWeight="medium">
                  Prescription
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={handleViewPrescription}
                  sx={{
                    textTransform: 'none',
                    borderColor: COLORS.primary.main,
                    color: COLORS.primary.main,
                    '&:hover': {
                      borderColor: COLORS.primary.dark,
                      backgroundColor: 'rgba(76, 10, 39, 0.04)',
                    },
                  }}
                >
                  View
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Items Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell>{t('orderManagement.editOrder.item')}</TableCell>
                <TableCell>{t('orderManagement.editOrder.store')}</TableCell>
                <TableCell align="center">{t('orderManagement.editOrder.quantity')}</TableCell>
                <TableCell align="right">{t('orderManagement.editOrder.unitPrice')}</TableCell>
                <TableCell align="right">{t('orderManagement.editOrder.total')}</TableCell>
                <TableCell align="center">{t('orderManagement.editOrder.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderItems.map((item: any, index: number) => (
                <TableRow key={item.itemId || index}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={Array.isArray(item.imageUrl) ? item.imageUrl[0] : item.imageUrl}
                        alt={item.itemName}
                        sx={{ width: 40, height: 40 }}
                        variant="rounded"
                      />
                      <Box>
                        <Typography fontWeight="medium">
                          {item.itemName}
                        </Typography>
                         {item.choices && item.choices.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {item.choices.map((choice: any, choiceIndex: number) => (
                              <Typography 
                                key={choiceIndex}
                                variant="caption" 
                                color="primary" 
                                sx={{ 
                                  display: 'block',
                                  fontStyle: 'italic',
                                  fontSize: '0.75rem'
                                }}
                              >
                                + {choice.choiceName} {choice.choicePrice > 0 && `(${formatPrice(choice.choicePrice)})`}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {/* Display meal information */}
                        {item.mealId && item.mealName && (
                          <Box sx={{ mt: 0.75, p: 0.75, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
                            <Typography 
                              variant="caption" 
                              color="secondary" 
                              sx={{ 
                                display: 'block',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                mb: 0.25
                              }}
                            >
                              🍽️ Meal: {item.mealName} {item.mealPrice > 0 && `(${formatPrice(item.mealPrice)})`}
                            </Typography>
                            {item.mealChoiceGroups && item.mealChoiceGroups.length > 0 && (
                              <Box sx={{ pl: 0.5 }}>
                                {item.mealChoiceGroups.map((group: any, groupIndex: number) => (
                                  <Typography 
                                    key={groupIndex}
                                    variant="caption" 
                                    sx={{ 
                                      display: 'block',
                                      fontSize: '0.65rem',
                                      color: 'text.secondary'
                                    }}
                                  >
                                    {group.choiceGroupName}: {group.choices?.map((c: any) => c.choiceName).join(', ')}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                        <Typography variant="body2" color="textSecondary" fontWeight="medium">
                          {t('orderManagement.editOrder.specialInstructions')} {item.specialInstructions && item.specialInstructions.trim() !== '' ? item.specialInstructions : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {item.storeName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {item.storeType}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight="medium">
                      {item.quantity}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium">
                      {formatPrice((() => {
                        // Prioritize discount prices over original price
                        const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
                        const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
                        const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
                        return (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
                      })())}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatPrice(calculateItemTotal(item))}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                  {(() => {
  const merchantControls = order?.merchantControls || {};
  const currentPreparationStatus = order?.merchantControls?.currentPreparationStatus;
  if (item.merchantAccepted === true) {
    return <Chip label={t('orderManagement.editOrder.accepted')} color="success" size="small" />;
  }
  if (item.merchantDenied === true) {
    return <Chip label={t('orderManagement.editOrder.rejected')} color="error" size="small" />;
  }
  const hasAvailableControls =
    merchantControls.canAcceptOrder ||
    merchantControls.canRejectOrder ||
    merchantControls.canPrepareOrder ||
    merchantControls.canMarkReadyForPickup ||
    merchantControls.canCancelOrder ||
    merchantControls.canMarkDelivered;
  const isPendingStatus =
    !selfPickupStatuses[item.itemId] ||
    selfPickupStatuses[item.itemId] === 'PENDING';
  const isMerchantAccepted =
    (order?.currentStatus || '').toLowerCase() === 'merchant accepted';
  if (hasAvailableControls || isPendingStatus || isMerchantAccepted) {
    const normalizedCurrent = (currentPreparationStatus || '').toUpperCase();
  //   const selectValue =
  // selfPickupStatuses[item.itemId] &&
  // selfPickupStatuses[item.itemId] !== ''
  //   ? selfPickupStatuses[item.itemId]
  //   : 'PENDING';
  

    return (
      <Select
      value={selfPickupStatuses[item.itemId] ?? 'PENDING'}
      onChange={(e) => handleSelfPickupChange(item.itemId, e.target.value)}
        size="small"
        displayEmpty
        variant="outlined"
        sx={{
          minWidth: 160,
          fontSize: 12,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#888' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
          '& .MuiSelect-select': {
            py: 0.8,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <MenuItem value="PENDING">
          <Chip label={t('orderManagement.editOrder.pending')} color="warning" size="small" />
        </MenuItem>
        {merchantControls.canAcceptOrder && (
          <MenuItem value="Accepted">
            <Chip label={t('orderManagement.editOrder.accepted')} color="success" size="small" />
          </MenuItem>
        )}
        {merchantControls.canRejectOrder && (
          <MenuItem value="Rejected">
            <Chip label={t('orderManagement.editOrder.rejected')} color="error" size="small" />
          </MenuItem>
        )}
        {merchantControls.canPrepareOrder && (
          <MenuItem value="ORDER_PREPARING">
            <Chip label={t('orderManagement.editOrder.preparing')} color="primary" size="small" />
          </MenuItem>
        )}
        {merchantControls.canMarkReadyForPickup && normalizedCurrent !== 'READY_FOR_PICKUP' && (
          <MenuItem value="READY_FOR_PICKUP">
            <Chip label={t('orderManagement.editOrder.readyForPickup')} color="info" size="small" />
          </MenuItem>
        )}
        {merchantControls.canMarkDelivered && !isSelfPickupOrderCompleted() && (
          <MenuItem value="MERCHANT_DELIVERED">
            <Chip label={t('orderManagement.editOrder.delivered')} color="success" size="small" />
          </MenuItem>
        )}
        {merchantControls.canCancelOrder && (
          <MenuItem value="MERCHANT_CANCELLED">
            <Chip label={t('orderManagement.editOrder.cancelled')} color="error" size="small" />
          </MenuItem>
        )}
      </Select>
    );
  }
  if (currentPreparationStatus && currentPreparationStatus !== 'PENDING') {
    const statusLabel =
      currentPreparationStatus === 'ORDER_PREPARING'
        ? 'Preparing'
        : currentPreparationStatus === 'READY_FOR_PICKUP'
        ? 'Ready for pickup'
        : currentPreparationStatus === 'MERCHANT_DELIVERED'
        ? 'Delivered'
        : currentPreparationStatus === 'MERCHANT_CANCELLED'
        ? 'Cancelled'
        : currentPreparationStatus;
    const statusColor =
      currentPreparationStatus === 'ORDER_PREPARING'
        ? 'primary'
        : currentPreparationStatus === 'READY_FOR_PICKUP'
        ? 'info'
        : currentPreparationStatus === 'MERCHANT_DELIVERED'
        ? 'success'
        : currentPreparationStatus === 'MERCHANT_CANCELLED'
        ? 'error'
        : 'default';
    return <Chip label={statusLabel} color={statusColor as any} size="small" />;
  }
  const itemStatus = selfPickupStatuses[item.itemId] ?? 'PENDING';

  return (
    <Select
      value={itemStatus}
      onChange={(e) => handleSelfPickupChange(item.itemId, e.target.value)}
      size="small"
      variant="outlined"
      sx={{ minWidth: 160 }}
    >
      {/* PENDING — always visible */}
      <MenuItem value="PENDING">
        <Chip label={t('orderManagement.editOrder.pending')} color="warning" size="small" />
      </MenuItem>
  
      {merchantControls.canAcceptOrder && (
        <MenuItem value="Accepted">
          <Chip label={t('orderManagement.editOrder.accepted')} color="success" size="small" />
        </MenuItem>
      )}
  
      {merchantControls.canRejectOrder && (
        <MenuItem value="Rejected">
          <Chip label={t('orderManagement.editOrder.rejected')} color="error" size="small" />
        </MenuItem>
      )}
  
      {merchantControls.canCancelOrder && (
        <MenuItem value="MERCHANT_CANCELLED">
          <Chip label={t('orderManagement.editOrder.cancelled')} color="error" size="small" />
        </MenuItem>
      )}
    </Select>
  );
  
})()}

                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary */}
        <Box mt={3} p={2} bgcolor="#f8f9fa" borderRadius={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" color="textSecondary">
            {t('orderManagement.editOrder.orderSummary')}
          </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 
                    order?.currentStatus?.toLowerCase().includes('cancelled') ? '#f44336' :
                    order?.currentStatus?.toLowerCase().includes('preparing') ? '#ff9800' :
                    order?.currentStatus?.toLowerCase().includes('delivered') ? '#4caf50' :
                    order?.currentStatus?.toLowerCase().includes('ready') ? '#2196f3' :
                    order?.currentStatus?.toLowerCase().includes('accepted') ? '#4caf50' :
                    order?.currentStatus?.toLowerCase().includes('placed') ? '#4caf50' : '#9e9e9e'
                }}
              />
              <Typography variant="body2" fontWeight="medium">
                {order?.currentStatus || 'Unknown'}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
            <Box flex={1}>
              <Typography variant="body2">
                {t('orderManagement.editOrder.totalItems')} {orderItems.length}
              </Typography>
            </Box>
            
            <Box flex={1}>
              <Typography variant="body2">
                {t('orderManagement.editOrder.orderAmount')} {formatPrice(order.orderAmount)}
              </Typography>
            </Box>
            {
              order.invoiceAmount && (
                <Box flex={1}>
                  <Typography variant="body2">
                    {t('orderManagement.editOrder.invoiceAmount')} {formatPrice(order.invoiceAmount)}
                  </Typography>
                </Box>
              )
            }
          </Box>
        </Box>

        {/* Self Pickup order-level selection removed as per requirement */}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isSubmitting}
        >
          CLOSE
        </Button>
        
        {/* Show SAVE CHANGES button when items are dropdowns (not chips) */}
        {!areAllItemsDisplayedAsChips() && (
        <Button
            onClick={async () => {
              if (!order) return;
              setIsSubmitting(true);
              try {
                // Prepare accepted and denied item IDs based on current selections
                const acceptedItemIds: string[] = [];
                const deniedItemIds: string[] = [];

                orderItems.forEach((item: any) => {
                  const selectedStatus = selfPickupStatuses[item.itemId] ?? 'PENDING';
                  selfPickupStatuses[item.itemId] = normalizeStatus(selectedStatus);
                
                  if (selectedStatus === 'Accepted') acceptedItemIds.push(item.itemId);
                  if (selectedStatus === 'Rejected' || selectedStatus === 'MERCHANT_CANCELLED') deniedItemIds.push(item.itemId);
                });
                
                
                
                
                const requestData = {
                  orderId: order.orderId,
                  orderNumber: order.orderNumber,
                  acceptedItemIds,
                  deniedItemIds,
                  status: 'MERCHANT_STATUS',
                  userId: JSON.parse(localStorage.getItem('userData') || '{}')?.userId || '',
                  userType: 'merchant',
                  reason: 'merchant accepted or denied'
                };

                await updateOrderStatus(requestData);
                onSuccess?.();
                onClose();
              } catch (error) {
                console.error('Error updating order status:', error);
              } finally {
                setIsSubmitting(false);
              }
            }}
          variant="contained"
            disabled={isSubmitting || areAllItemsPending() || isOrderCancelled}
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
            }}
          >
            {t('orderManagement.editOrder.saveChanges')}
        </Button>
        )}

        {/* Show individual action buttons when items are chips */}
        {areAllItemsDisplayedAsChips() && order?.merchantControls?.canPrepareOrder && (
          <Button
            onClick={async () => {
              if (!order) return;
              setIsSubmitting(true);
              try {
                const requestData = {
                  orderId: order.orderId,
                  orderNumber: order.orderNumber,
                  itemIds: order.orderItems?.filter((item: any) => item.merchantAccepted === true).map((item: any) => item.itemId) || [],
                  status: 'ORDER_PREPARING',
                  userId: JSON.parse(localStorage.getItem('userData') || '{}')?.userId || '',
                  userType: 'merchant',
                  reason: 'order preparing (self pickup/delivery)'
                };
                await updateOrderStatus(requestData);
                onSuccess?.();
                onClose();
              } catch (error) {
                console.error('Error updating order status:', error);
              } finally {
                setIsSubmitting(false);
              }
            }}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#9c27b0',
              color: 'white',
              '&:hover': {
                backgroundColor: '#7b1fa2',
              },
            }}
          >
            PREPARING ORDER
          </Button>
        )}
        {areAllItemsDisplayedAsChips() && order?.merchantControls?.canMarkReadyForPickup && (
          <Button
            onClick={async () => {
              if (!order) return;
              setIsSubmitting(true);
              try {
                const requestData = {
                  orderId: order.orderId,
                  orderNumber: order.orderNumber,
                  itemIds: order.orderItems?.filter((item: any) => item.merchantAccepted === true).map((item: any) => item.itemId) || [],
                  status: 'READY_FOR_PICKUP',
                  userId: JSON.parse(localStorage.getItem('userData') || '{}')?.userId || '',
                  userType: 'merchant',
                  reason: 'ready for pickup (self pickup/delivery)'
                };
                await updateOrderStatus(requestData);
                onSuccess?.();
                onClose();
              } catch (error) {
                console.error('Error updating order status:', error);
              } finally {
                setIsSubmitting(false);
              }
            }}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#2196f3',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1976d2',
              },
            }}
          >
            {t('orderManagement.editOrder.readyForPickup')}
          </Button>
        )}
        {areAllItemsDisplayedAsChips() && order?.merchantControls?.canMarkDelivered && !isSelfPickupOrderCompleted() && (
          <Button
            onClick={async () => {
              if (!order) return;
              setIsSubmitting(true);
              try {
                const requestData = {
                  orderId: order.orderId,
                  orderNumber: order.orderNumber,
                  itemIds: order.orderItems?.filter((item: any) => item.merchantAccepted === true).map((item: any) => item.itemId) || [],
                  status: 'MERCHANT_DELIVERED',
                  userId: JSON.parse(localStorage.getItem('userData') || '{}')?.userId || '',
                  userType: 'merchant',
                  reason: 'merchant delivered (self pickup/delivery)'
                };
                await updateOrderStatus(requestData);
                onSuccess?.();
                onClose();
              } catch (error) {
                console.error('Error updating order status:', error);
              } finally {
                setIsSubmitting(false);
              }
            }}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#4caf50',
              color: 'white',
              '&:hover': {
                backgroundColor: '#388e3c',
              },
            }}
          >
            DELIVER ORDER
          </Button>
        )}
        {areAllItemsDisplayedAsChips() && order?.merchantControls?.canCancelOrder && (
          <Button
            onClick={async () => {
              if (!order) return;
              setIsSubmitting(true);
              try {
                const requestData = {
                  orderId: order.orderId,
                  orderNumber: order.orderNumber,
                  itemIds: order.orderItems?.filter((item: any) => item.merchantAccepted === true).map((item: any) => item.itemId) || [],
                  status: 'MERCHANT_CANCELLED',
                  userId: JSON.parse(localStorage.getItem('userData') || '{}')?.userId || '',
                  userType: 'merchant',
                  reason: 'merchant cancelled (self pickup/delivery)'
                };
                await updateOrderStatus(requestData);
                onSuccess?.();
                onClose();
              } catch (error) {
                console.error('Error updating order status:', error);
              } finally {
                setIsSubmitting(false);
              }
            }}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#f44336',
              color: 'white',
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
            }}
          >
            CANCEL ORDER
          </Button>
        )}
      </DialogActions>

      {/* Prescription Dialog */}
      <Dialog
        open={prescriptionDialogOpen}
        onClose={handleClosePrescriptionDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Typography variant="h6" fontWeight="bold">
            Prescription - Order #{order?.orderNumber}
          </Typography>
          <Button onClick={handleClosePrescriptionDialog} startIcon={<Close />}>
            Close
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {order?.prescriptionUrl ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Prescription Document
              </Typography>
              
              {/* Document Viewer */}
              <Box sx={{ 
                width: '100%', 
                height: '600px', 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                {order.prescriptionUrl.toLowerCase().includes('.pdf') ? (
                  <iframe
                    src={order.prescriptionUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    title="Prescription Document"
                  />
                ) : (
                  <img
                    src={order.prescriptionUrl}
                    alt="Prescription Document"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const container = target.parentElement;
                      if (container) {
                        container.innerHTML = `
                          <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666;">
                            <div style="text-align: center;">
                              <p>Unable to display this file type</p>
                              <p style="font-size: 12px;">Please download to view</p>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                )}
              </Box>

              {/* Download Button */}
              {/* <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => window.open(order.prescriptionUrl, '_blank')}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  Download Prescription
                </Button>
              </Box> */}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                No Prescription Available
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                This order does not have a prescription document.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default OrderEditModal; 
